import { useState, FormEvent } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';

export default function Contact() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d630m55si3r5mpi9uo70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', organization: '', projectType: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 bg-white/5 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-2 h-2 rounded-full bg-[#ff6b4a]"></div>
              <span className="text-[#f5f1e8]/70 text-sm font-medium uppercase tracking-wider">{t('contact.badge')}</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
              {t('contact.title')}
            </h2>
            
            <p className="text-[#f5f1e8]/80 text-lg mb-8">
              {t('contact.description')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="name"
                  placeholder={t('contact.form.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-6 py-4 bg-[#f5f1e8] text-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b4a] placeholder-gray-500"
                />
                <input
                  type="email"
                  name="email"
                  placeholder={t('contact.form.email')}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-6 py-4 bg-[#f5f1e8] text-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b4a] placeholder-gray-500"
                />
              </div>
              
              <input
                type="text"
                name="organization"
                placeholder={t('contact.form.organization')}
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                required
                className="w-full px-6 py-4 bg-[#f5f1e8] text-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b4a] placeholder-gray-500"
              />
              
              <select
                name="projectType"
                value={formData.projectType}
                onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                required
                className="w-full px-6 py-4 bg-[#f5f1e8] text-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b4a] cursor-pointer"
              >
                <option value="">{t('contact.form.projectType')}</option>
                <option value="application">{t('contact.form.projectType.app')}</option>
                <option value="database">{t('contact.form.projectType.database')}</option>
                <option value="analytics">{t('contact.form.projectType.analytics')}</option>
                <option value="gis">{t('contact.form.projectType.gis')}</option>
                <option value="cloud">{t('contact.form.projectType.cloud')}</option>
                <option value="training">{t('contact.form.projectType.training')}</option>
                <option value="other">{t('contact.form.projectType.other')}</option>
              </select>
              
              <textarea
                name="message"
                placeholder={t('contact.form.message')}
                value={formData.message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, message: e.target.value });
                  }
                }}
                required
                rows={6}
                maxLength={500}
                className="w-full px-6 py-4 bg-[#f5f1e8] text-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b4a] placeholder-gray-500 resize-none"
              ></textarea>
              <div className="text-[#f5f1e8]/60 text-sm text-right">
                {formData.message.length}/500 {t('contact.form.characters')}
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-[#1a4d2e] text-white rounded-lg font-medium hover:bg-[#0f3820] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-lg"
              >
                {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-200 text-center">
                  {t('contact.form.success')}
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-center">
                  {t('contact.form.error')}
                </div>
              )}
            </form>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-[#1a4d2e]/30 backdrop-blur-sm rounded-3xl p-8 border border-white/10 sticky top-24">
              <h3 className="text-2xl font-bold text-white mb-8">{t('contact.info.title')}</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#ff6b4a]/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-[#ff6b4a]"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">{t('contact.info.location')}</h4>
                    <p className="text-[#f5f1e8]/70">Rua Travessa Castro Alves, 6, Quadra 6, Bairro Centro, Novo Airão, AM. CEP 69.730-000. Brasil</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#ff6b4a]/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-[#ff6b4a]"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">{t('contact.info.email')}</h4>
                    <a href="mailto:ecopacheco22@gmail.com" className="text-[#f5f1e8]/70 hover:text-[#ff6b4a] transition-colors cursor-pointer">ecopacheco22@gmail.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#ff6b4a]/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-[#ff6b4a]"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">{t('contact.info.phone')}</h4>
                    <a href="https://wa.me/5548991550984" target="_blank" rel="noopener noreferrer" className="text-[#f5f1e8]/70 hover:text-[#ff6b4a] transition-colors cursor-pointer">+55 48 9-9155-0984</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
